import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Chip, Stack, Pagination, TableSortLabel, Tooltip,
} from '@mui/material';
import { currency, fmtDate } from './format';

const COLUMNS = [
  { id: 'business_name', label: 'Business', sortable: true },
  { id: 'totalCommissions', label: 'Commissions', sortable: true, align: 'right' },
  { id: 'fundingCount', label: 'Fundings', sortable: true, align: 'right' },
  { id: 'firstDeposit', label: 'First Deposit', sortable: true },
  { id: 'lastDeposit', label: 'Last Deposit', sortable: true },
  { id: 'lenders', label: 'Lenders', sortable: false },
];

export default function ClientTable({
  rows = [], total = 0, page = 1, pages = 1, sortBy, order = 'desc',
  onSort, onPageChange, onOpen,
}) {
  return (
    <Box>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell
                  key={c.id}
                  align={c.align || 'left'}
                  sx={{ fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                >
                  {c.sortable && onSort ? (
                    <TableSortLabel
                      active={sortBy === c.id}
                      direction={sortBy === c.id ? order : 'asc'}
                      onClick={() => onSort(c.id)}
                    >
                      {c.label}
                    </TableSortLabel>
                  ) : c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.client_id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpen?.(r)}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 240 }} title={r.business_name}>
                    {r.business_name || r.client_id}
                  </Typography>
                  {r.noteCount > 0 && (
                    <Typography variant="caption" color="text.secondary">{r.noteCount} note{r.noteCount > 1 ? 's' : ''}</Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700} color="success.main">{currency(r.totalCommissions)}</Typography>
                </TableCell>
                <TableCell align="right">{r.fundingCount || 0}</TableCell>
                <TableCell>{fmtDate(r.firstDeposit)}</TableCell>
                <TableCell>{fmtDate(r.lastDeposit)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, maxWidth: 260 }}>
                    {(r.lenders || []).slice(0, 3).map((l) => (
                      <Chip key={l} label={l} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    ))}
                    {(r.lenders || []).length > 3 && (
                      <Tooltip title={r.lenders.slice(3).join(', ')}>
                        <Chip label={`+${r.lenders.length - 3}`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No funded clients found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">{total} client{total === 1 ? '' : 's'}</Typography>
        {pages > 1 && (
          <Pagination count={pages} page={page} onChange={(_, p) => onPageChange?.(p)} size="small" />
        )}
      </Stack>
    </Box>
  );
}
